import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Paper,
  Grid,
  Card,
  CardContent,
  Avatar,
  Badge,
  Stack
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Link as LinkIcon,
  CheckCircle as CheckCircleIcon,
  Build as BuildIcon,
  Cable as CableIcon,
  Settings as SettingsIcon,
  Print as PrintIcon,
  Description as DescriptionIcon,
  Info as InfoIcon,
  Engineering as EngineeringIcon,
  Palette as PaletteIcon
} from '@mui/icons-material';
import {
  createBOMLink,
  exportBOMAsJSON,
  canGenerateBOM,
  generateBOM
} from '../services/bomGeneratorService';
import ClipboardService from '../services/clipboardService';

export default function BOMDialog({ open, onClose, config }) {
  const [customName, setCustomName] = useState('');
  const [inputValue, setInputValue] = useState(''); // Separate state for input that doesn't trigger BOM regeneration
  const [generating, setGenerating] = useState(false);
  const [bomLink, setBomLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [bomData, setBomData] = useState(null);
  const [loadingBOM, setLoadingBOM] = useState(false);

  // Generate BOM data immediately when dialog opens
  useEffect(() => {
    if (open && config && canGenerateBOM(config)) {
      const generateInitialBOM = async () => {
        setLoadingBOM(true);
        try {
          const bom = await generateBOM(config, customName || null);
          setBomData(bom);
        } catch (error) {
          console.error('Error generating initial BOM:', error);
          setError('Failed to generate BOM preview');
        } finally {
          setLoadingBOM(false);
        }
      };
      
      generateInitialBOM();
    }
  }, [open, config, customName]); // Only depend on customName, not inputValue

  // Sync input value with customName when dialog opens
  useEffect(() => {
    if (open) {
      setInputValue(customName);
    }
  }, [open, customName]);

  // Handle when user finishes typing (blur, enter, or generate)
  const handleNameFinalized = () => {
    setCustomName(inputValue);
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleNameFinalized();
    }
  };

  const handleGenerateBOM = async () => {
    if (!canGenerateBOM(config)) {
      setError('Cannot generate BOM for this cable configuration');
      return;
    }

    // Finalize the name before generating
    handleNameFinalized();

    setGenerating(true);
    setError('');
    
    try {
      // Generate BOM link
      const linkResult = await createBOMLink(config, customName || null);
      setBomLink(linkResult);
      
      // Generate full BOM data for display
      const bom = await generateBOM(config, customName || null);
      setBomData(bom);
      
    } catch (error) {
      console.error('Error generating BOM:', error);
      setError('Failed to generate BOM. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyLink = async () => {
    if (!bomLink?.url) return;
    
    try {
      const success = await ClipboardService.copyToClipboard(bomLink.url);
      if (success) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        setError(''); // Clear any previous errors
      } else {
        setError('Please copy the link manually from the dialog');
      }
    } catch (error) {
      console.error('Failed to copy link:', error);
      setError('Failed to copy link to clipboard');
    }
  };

  const handleExportJSON = async () => {
    if (!canGenerateBOM(config)) {
      setError('Cannot export BOM for this cable configuration');
      return;
    }

    setGenerating(true);
    setError('');
    
    try {
      await exportBOMAsJSON(config, customName || null);
    } catch (error) {
      console.error('Error exporting BOM:', error);
      setError('Failed to export BOM. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handlePrintBOM = () => {
    if (!bomData) return;
    
    // Create a new window for printing with the PrintableBOM component
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Bill of Materials - ${bomData.name}</title>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <link rel="stylesheet" href="https://fonts.googleapis.com/css?family=Roboto:300,400,500,700&display=swap" />
          <link rel="stylesheet" href="https://fonts.googleapis.com/icon?family=Material+Icons" />
          <style>
            * { box-sizing: border-box; }
            body { 
              margin: 0; 
              padding: 0; 
              font-family: 'Roboto', Arial, sans-serif;
              background: white;
            }
            @media print {
              body { margin: 0; padding: 0; }
              .no-print { display: none !important; }
            }
          </style>
        </head>
        <body>
          <div id="printable-bom"></div>
          <script src="https://unpkg.com/react@18/umd/react.production.min.js"></script>
          <script src="https://unpkg.com/react-dom@18/umd/react-dom.production.min.js"></script>
          <script src="https://unpkg.com/@babel/standalone/babel.min.js"></script>
          <script type="text/babel">
            // Simple print component for the BOM
            const bomData = ${JSON.stringify(bomData)};
            
            function PrintBOM() {
              const formatDate = (timestamp) => {
                return new Date(timestamp).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                });
              };
              
              return (
                <div style={{
                  width: '100%',
                  maxWidth: '800px',
                  margin: '0 auto',
                  padding: '20px',
                  fontFamily: 'Arial, sans-serif'
                }}>
                  {/* Header */}
                  <div style={{
                    padding: '20px',
                    marginBottom: '20px',
                    textAlign: 'center',
                    borderBottom: '3px solid #1976d2',
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
                  }}>
                    <h1 style={{
                      fontWeight: 700,
                      color: '#2c3e50',
                      marginBottom: '10px',
                      fontSize: '28px'
                    }}>
                      Bill of Materials
                    </h1>
                    <h2 style={{
                      color: '#34495e',
                      marginBottom: '15px',
                      fontSize: '20px'
                    }}>
                      {bomData.name}
                    </h2>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap' }}>
                      <div><strong>SKU:</strong> <span style={{ fontFamily: 'monospace' }}>{bomData.sku}</span></div>
                      <div><strong>BOM ID:</strong> <span style={{ fontFamily: 'monospace' }}>{bomData.id}</span></div>
                      <div><strong>Generated:</strong> {formatDate(bomData.timestamp)}</div>
                    </div>
                  </div>

                  {/* Technical Specifications */}
                  <div style={{
                    padding: '15px',
                    marginBottom: '20px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px'
                  }}>
                    <h3 style={{
                      color: '#1976d2',
                      fontWeight: 600,
                      marginBottom: '15px',
                      fontSize: '18px',
                      borderBottom: '1px solid #e0e0e0',
                      paddingBottom: '5px'
                    }}>
                      Technical Specifications
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <span style={{ fontWeight: 600 }}>Cable Type:</span>
                        <span>{bomData.specifications.cableType}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <span style={{ fontWeight: 600 }}>Category:</span>
                        <span>{bomData.specifications.cableCategory}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <span style={{ fontWeight: 600 }}>Style:</span>
                        <span>{bomData.specifications.cableStyle}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <span style={{ fontWeight: 600 }}>Length:</span>
                        <span>{bomData.specifications.length.value} {bomData.specifications.length.unit}</span>
                      </div>
                      ${bomData.specifications.wireGauge ? `
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <span style={{ fontWeight: 600 }}>Wire Gauge:</span>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{ marginRight: '8px' }}>{bomData.specifications.wireGauge.gauge} AWG</span>
                          <div style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            backgroundColor: '${bomData.specifications.wireGauge.color?.toLowerCase() || '#000'}',
                            border: '1px solid #ccc'
                          }}></div>
                        </div>
                      </div>
                      ` : ''}
                    </div>
                  </div>

                  {/* Components */}
                  <div style={{
                    padding: '15px',
                    marginBottom: '20px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px'
                  }}>
                    <h3 style={{
                      color: '#1976d2',
                      fontWeight: 600,
                      marginBottom: '15px',
                      fontSize: '18px',
                      borderBottom: '1px solid #e0e0e0',
                      paddingBottom: '5px'
                    }}>
                      Components (${bomData.components.length})
                    </h3>
                    <div>
                      ${bomData.components.map(component => `
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '10px',
                          marginBottom: '8px',
                          border: '1px solid #e0e0e0',
                          borderRadius: '4px',
                          backgroundColor: '#fafafa'
                        }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, marginBottom: '4px' }}>${component.description}</div>
                            <div style={{ color: '#666', fontSize: '14px' }}>${component.specification}</div>
                          </div>
                          <div style={{
                            backgroundColor: '#1976d2',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontWeight: 600,
                            minWidth: '60px',
                            textAlign: 'center'
                          }}>
                            Qty: ${component.quantity}
                          </div>
                        </div>
                      `).join('')}
                    </div>
                  </div>

                  {/* Footer */}
                  <div style={{
                    textAlign: 'center',
                    marginTop: '30px',
                    paddingTop: '20px',
                    borderTop: '2px solid #e0e0e0'
                  }}>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                      Generated by NLFX Cable Builder | Professional Cable Solutions
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      This document contains detailed specifications for cable assembly and should be used for manufacturing and quality control purposes.
                    </div>
                  </div>
                </div>
              );
            }
            
            ReactDOM.render(<PrintBOM />, document.getElementById('printable-bom'));
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    
    // Wait for content to load then print
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const handleClose = () => {
    setCustomName('');
    setBomLink(null);
    setCopied(false);
    setError('');
    setBomData(null);
    onClose();
  };

  const getCableTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'power': return <EngineeringIcon />;
      case 'audio': return <CableIcon />;
      case 'video': return <CableIcon />;
      case 'data': return <CableIcon />;
      case 'dmx': return <CableIcon />;
      default: return <CableIcon />;
    }
  };

  const getCableTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'power': return 'error';
      case 'audio': return 'primary';
      case 'video': return 'secondary';
      case 'data': return 'info';
      case 'dmx': return 'warning';
      default: return 'default';
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '95vh',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: '2px solid #e3f2fd',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        color: '#2c3e50'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar sx={{ bgcolor: '#1976d2', mr: 2 }}>
            <BuildIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Bill of Materials (BOM)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Professional cable specification document
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: '#2c3e50' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!canGenerateBOM(config) && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Configuration Required:</strong> BOM generation requires a complete cable configuration with connectors selected.
            </Typography>
          </Alert>
        )}

        {/* BOM Preview Section - Show immediately */}
        {loadingBOM && (
          <Box sx={{ mb: 4, textAlign: 'center', py: 4 }}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Generating Bill of Materials...
            </Typography>
          </Box>
        )}

        {bomData && !loadingBOM && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3, color: '#1976d2' }}>
              <BuildIcon sx={{ mr: 1 }} />
              Bill of Materials Preview
            </Typography>
            
            <Grid container spacing={3}>
              {/* Header Information */}
              <Grid item xs={12}>
                <Card sx={{ border: '2px solid #e3f2fd', bgcolor: '#f8fbff' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                        <InfoIcon sx={{ mr: 1 }} />
                        {bomData.name}
                      </Typography>
                      <Chip 
                        icon={getCableTypeIcon(bomData.specifications.cableType)}
                        label={bomData.specifications.cableType}
                        color={getCableTypeColor(bomData.specifications.cableType)}
                        variant="outlined"
                      />
                    </Box>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">SKU</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                          {bomData.sku}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">BOM ID</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                          {bomData.id}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">Generated</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {new Date(bomData.timestamp).toLocaleString()}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Technical Specifications */}
              <Grid item xs={12} md={6}>
                <Card sx={{ border: '1px solid #e0e0e0', height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <EngineeringIcon sx={{ mr: 1 }} />
                      Technical Specifications
                    </Typography>
                    
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Cable Type:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {bomData.specifications.cableType}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Category:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {bomData.specifications.cableCategory}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Style:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {bomData.specifications.cableStyle}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Length:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {bomData.specifications.length.value} {bomData.specifications.length.unit}
                        </Typography>
                      </Box>
                      {bomData.specifications.wireGauge && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary">Wire Gauge:</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mr: 1 }}>
                              {bomData.specifications.wireGauge.gauge} AWG
                            </Typography>
                            <Box sx={{
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              backgroundColor: bomData.specifications.wireGauge.color || '#000',
                              border: '1px solid #ccc'
                            }} />
                          </Box>
                        </Box>
                      )}
                      {bomData.specifications.wireType && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary">Wire Type:</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mr: 1 }}>
                              {bomData.specifications.wireType}
                            </Typography>
                            <Box sx={{
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              backgroundColor: '#4a5568',
                              border: '1px solid #ccc',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: '0.7rem',
                              fontWeight: 'bold'
                            }}>
                              W
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Components List */}
              <Grid item xs={12} md={6}>
                <Card sx={{ border: '1px solid #e0e0e0', height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <BuildIcon sx={{ mr: 1 }} />
                      Components ({bomData.components.length})
                    </Typography>
                    
                    <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                      {bomData.components.map((component, index) => (
                        <ListItem key={index} sx={{ px: 0, py: 1 }}>
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: '#f0f0f0' }}>
                              {component.type === 'cable' && <CableIcon fontSize="small" />}
                              {component.type === 'connector' && <SettingsIcon fontSize="small" />}
                              {component.type === 'wire' && <CableIcon fontSize="small" />}
                              {component.type === 'finishing' && <PaletteIcon fontSize="small" />}
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {component.description}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                {component.specification}
                              </Typography>
                            }
                          />
                          <Badge 
                            badgeContent={component.quantity} 
                            color="primary"
                            sx={{ '& .MuiBadge-badge': { fontSize: '0.75rem' } }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Configuration Section */}
        <Card sx={{ mb: 3, border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <DescriptionIcon sx={{ mr: 1 }} />
              BOM Configuration
            </Typography>
            
            <TextField
              fullWidth
              label="Custom BOM Name (Optional)"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onBlur={handleNameFinalized}
              onKeyPress={handleKeyPress}
              placeholder="e.g., Studio Monitor Cable, Live Sound Snake, Power Distribution Cable"
              sx={{ mb: 3 }}
              helperText="Give your BOM a descriptive name for easy identification"
            />
            
            <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={generating ? <CircularProgress size={20} /> : <LinkIcon />}
                onClick={handleGenerateBOM}
                disabled={generating || !canGenerateBOM(config)}
                sx={{ minWidth: 200 }}
              >
                {generating ? 'Generating...' : 'Generate BOM Link'}
              </Button>
              
              <Button
                variant="outlined"
                startIcon={generating ? <CircularProgress size={20} /> : <DownloadIcon />}
                onClick={handleExportJSON}
                disabled={generating || !canGenerateBOM(config)}
                sx={{ minWidth: 150 }}
              >
                {generating ? 'Exporting...' : 'Export JSON'}
              </Button>

              {bomData && (
                <Button
                  variant="outlined"
                  startIcon={<PrintIcon />}
                  onClick={handlePrintBOM}
                  sx={{ minWidth: 120 }}
                >
                  Print BOM
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Shareable Link Section */}
        {bomLink && (
          <Card sx={{ mb: 3, border: '1px solid #e8f5e8', bgcolor: '#f8fff8' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: '#2e7d32' }}>
                <LinkIcon sx={{ mr: 1 }} />
                Shareable BOM Link
              </Typography>
              
              <Paper sx={{ p: 2, bgcolor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    fullWidth
                    value={bomLink.url}
                    InputProps={{ readOnly: true }}
                    size="small"
                    sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.875rem' } }}
                  />
                  <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
                    <IconButton 
                      onClick={handleCopyLink}
                      color={copied ? 'success' : 'primary'}
                      sx={{ bgcolor: copied ? '#e8f5e8' : '#f0f8ff' }}
                    >
                      {copied ? <CheckCircleIcon /> : <CopyIcon />}
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Share this link to allow others to view and reconstruct this cable configuration
                </Typography>
              </Paper>
            </CardContent>
          </Card>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 4, pt: 0 }}>
        <Button onClick={handleClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
} 